import { Form, Formik } from 'formik'
import React from 'react'
import * as Yup from 'yup'
import {
  COLORS,
  FieldError,
  Label,
  SectionLabel,
  ToggleRow,
  handleInputBlur,
  handleInputFocus,
  inputStyle,
  selectStyle,
} from '../styleHelpers'
import { COUNTRY_OPTIONS, STATE_OPTIONS } from '../../../../constants/address'
import type { AddressStepValues } from '../types'

export const EDIT_LOCATION_ADDRESS_FORM_ID = 'editLocationAddressForm'

const formatPhone = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 10)
  if (digits.length <= 3) return digits
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

const schema = Yup.object({
  phone: Yup.string()
    .trim()
    .required('Please enter a phone number')
    .test(
      'phone-length',
      'Phone must be at least 10 digits',
      (value) => (value ?? '').replace(/\D/g, '').length >= 10,
    ),
  address1: Yup.string().trim().required('Please enter address line 1'),
  city: Yup.string().trim().required('Please enter the city'),
  state: Yup.string().trim().required('Please select a province / state'),
  country: Yup.string().trim().required('Please select a country'),
  postalCode: Yup.string()
    .trim()
    .required('Please enter the postal / zip code'),
})

interface Props {
  initialValues: AddressStepValues
  onSubmit: (values: AddressStepValues) => void
}

export const AddressStep: React.FC<Props> = ({ initialValues, onSubmit }) => (
  <Formik
    initialValues={initialValues}
    enableReinitialize
    validationSchema={schema}
    onSubmit={(values) =>
      onSubmit({
        ...values,
        phone: values.phone.replace(/\D/g, ''),
        address1: values.address1.trim(),
        address2: values.address2.trim(),
        city: values.city.trim(),
        state: values.state.trim(),
        postalCode: values.postalCode.trim(),
        license: values.license.trim(),
      })
    }
  >
    {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => {
      const stateOptions = STATE_OPTIONS[values.country] || []
      const err = (k: keyof AddressStepValues): boolean =>
        !!(touched[k] && errors[k])
      const msg = (k: keyof AddressStepValues) =>
        touched[k] ? (errors[k] as string | undefined) : undefined

      return (
        <Form id={EDIT_LOCATION_ADDRESS_FORM_ID}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '14px 16px',
            }}
          >
            <div>
              <Label htmlFor="addloc-phone" required>
                Phone
              </Label>
              <input
                id="addloc-phone"
                name="phone"
                type="tel"
                value={formatPhone(values.phone)}
                placeholder="(555) 000-0000"
                onChange={(e) =>
                  setFieldValue('phone', e.target.value.replace(/\D/g, ''))
                }
                onBlur={(e) => {
                  handleBlur(e)
                  handleInputBlur(err('phone'))(e)
                }}
                onFocus={handleInputFocus}
                style={inputStyle(err('phone'))}
              />
              <FieldError msg={msg('phone')} />
            </div>

            <div>
              <Label htmlFor="addloc-license">License #</Label>
              <input
                id="addloc-license"
                name="license"
                value={values.license}
                placeholder="If applicable"
                onChange={handleChange}
                onBlur={(e) => {
                  handleBlur(e)
                  handleInputBlur(false)(e)
                }}
                onFocus={handleInputFocus}
                style={inputStyle(false)}
              />
            </div>

            <div style={{ gridColumn: 'span 2' }}>
              <SectionLabel>Address</SectionLabel>
            </div>

            <div style={{ gridColumn: 'span 2' }}>
              <Label htmlFor="addloc-address1" required>
                Address line 1
              </Label>
              <input
                id="addloc-address1"
                name="address1"
                value={values.address1}
                placeholder="123 Main St"
                onChange={handleChange}
                onBlur={(e) => {
                  handleBlur(e)
                  handleInputBlur(err('address1'))(e)
                }}
                onFocus={handleInputFocus}
                style={inputStyle(err('address1'))}
              />
              <FieldError msg={msg('address1')} />
            </div>

            <div style={{ gridColumn: 'span 2' }}>
              <Label htmlFor="addloc-address2">Address line 2</Label>
              <input
                id="addloc-address2"
                name="address2"
                value={values.address2}
                placeholder="Apt, suite, unit (optional)"
                onChange={handleChange}
                onBlur={(e) => {
                  handleBlur(e)
                  handleInputBlur(false)(e)
                }}
                onFocus={handleInputFocus}
                style={inputStyle(false)}
              />
            </div>

            <div>
              <Label htmlFor="addloc-city" required>
                City
              </Label>
              <input
                id="addloc-city"
                name="city"
                value={values.city}
                onChange={handleChange}
                onBlur={(e) => {
                  handleBlur(e)
                  handleInputBlur(err('city'))(e)
                }}
                onFocus={handleInputFocus}
                style={inputStyle(err('city'))}
              />
              <FieldError msg={msg('city')} />
            </div>

            <div>
              <Label htmlFor="addloc-country" required>
                Country
              </Label>
              <select
                id="addloc-country"
                name="country"
                value={values.country}
                onChange={(e) => {
                  const next = e.target.value
                  const nextOptions = STATE_OPTIONS[next] || []
                  const stillValid = nextOptions.some(
                    (o) => o.value === values.state,
                  )
                  setFieldValue('country', next)
                  if (!stillValid) setFieldValue('state', '')
                }}
                onBlur={(e) => {
                  handleBlur(e)
                  handleInputBlur(err('country'))(e)
                }}
                onFocus={handleInputFocus}
                style={selectStyle(err('country'))}
              >
                {COUNTRY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <FieldError msg={msg('country')} />
            </div>

            <div>
              <Label htmlFor="addloc-state" required>
                Province / State
              </Label>
              <select
                id="addloc-state"
                name="state"
                value={values.state}
                onChange={handleChange}
                onBlur={(e) => {
                  handleBlur(e)
                  handleInputBlur(err('state'))(e)
                }}
                onFocus={handleInputFocus}
                style={selectStyle(err('state'))}
              >
                <option value="">Select…</option>
                {stateOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <FieldError msg={msg('state')} />
            </div>

            <div>
              <Label htmlFor="addloc-postal" required>
                Postal / Zip code
              </Label>
              <input
                id="addloc-postal"
                name="postalCode"
                value={values.postalCode}
                onChange={handleChange}
                onBlur={(e) => {
                  handleBlur(e)
                  handleInputBlur(err('postalCode'))(e)
                }}
                onFocus={handleInputFocus}
                style={inputStyle(err('postalCode'))}
              />
              <FieldError msg={msg('postalCode')} />
            </div>

            <div style={{ gridColumn: 'span 2', marginTop: 4 }}>
              <ToggleRow
                label="Show this location on the website"
                description="When off, the location is saved as a draft and won't appear to customers until you publish it."
                checked={values.locationVisible}
                onChange={(v) => setFieldValue('locationVisible', v)}
              />
            </div>
          </div>

          {/* Surface a single combined error if any required field is missing. */}
          {Object.keys(errors).length > 0 &&
            Object.keys(touched).length > 0 && (
              <div
                style={{
                  marginTop: 14,
                  padding: '8px 10px',
                  borderRadius: 6,
                  background: COLORS.dangerSoft,
                  border: `1px solid ${COLORS.dangerBorder}`,
                  color: COLORS.danger,
                  fontSize: 12,
                  fontFamily: 'inherit',
                }}
              >
                Please fix the highlighted fields above.
              </div>
            )}
        </Form>
      )
    }}
  </Formik>
)
